import React, { Fragment, useState, useEffect } from 'react';
import { Row, Col, Input, Button } from 'reactstrap';
import { ModalButtonBuilder, ButtonBuilder, ToastBuilder, ApiRequest, CommonModal } from '../../../../../../../../GlobalComponents/Builders';
import { Copy, ArrowUp, ArrowDown, RotateCcw, Save } from 'react-feather';
import { handleCopy } from '../../../../../../../../GlobalComponents/Helpers';
import CreatableMultiSelect from '../../../../../../../../GlobalComponents/Common/Component/CreatableMultiSelect';
import useConfirmModal from '../../../../../../../../GlobalComponents/Builders/ConfirmModal';

/**
 * CacheFieldsSection — Manage per-job cache field configuration (dmt_job_setting with type='cache').
 *
 * Stored as a single setting with key='cache_fields' and value=JSON array of field configs.
 * The Code 30 mapping references this via global fallback when no explicit fields are provided.
 *
 * Field types:
 * - Scalar: "status/name" — tracks a single value
 * - Object: {"requester_info": ["name", "email"]} — tracks specific sub-keys of an object
 * - Object (all): {"requester_info": "*"} — tracks all sub-keys
 * - Array: {"requested_items": {"trackBy": "id", "track": ["stage", "service_item_name"]}} — tracks array items by ID
 */

const FIELD_TYPE_OPTIONS = [
    { label: 'Scalar', value: 'scalar', description: 'Track a single value (e.g., status, priority)' },
    { label: 'Object', value: 'object', description: 'Track sub-keys of an object (e.g., requester_info)' },
    { label: 'Array', value: 'array', description: 'Track items in an array by ID (e.g., requested_items)' },
    { label: 'Persistent', value: 'persistent', description: 'Computed values that persist across runs (not from source data)' },
];

const fieldTypeColor = { scalar: '#6c757d', object: '#0d6efd', array: '#198754', persistent: '#6f42c1' };

const detectFieldType = (entry) => {
    if (typeof entry === 'string') return 'scalar';
    if (typeof entry === 'object' && entry !== null) {
        if ('_persistent_data_' in entry) return 'persistent';
        const values = Object.values(entry);
        if (values.length === 1 && typeof values[0] === 'object' && values[0] !== null && 'trackBy' in values[0]) return 'array';
        return 'object';
    }
    return 'scalar';
};

const getFieldPath = (entry) => {
    if (typeof entry === 'string') return entry;
    if (typeof entry === 'object') return Object.keys(entry)[0] || '';
    return '';
};

const getFieldDetails = (entry) => {
    if (typeof entry === 'string') return null;
    if (typeof entry === 'object') {
        const value = Object.values(entry)[0];
        return value;
    }
    return null;
};

const formatFieldDisplay = (entry) => {
    const type = detectFieldType(entry);
    const path = getFieldPath(entry);
    const details = getFieldDetails(entry);

    if (type === 'scalar') return path;
    if (type === 'persistent') {
        const keys = Array.isArray(details) ? details : [];
        return keys.length > 0 ? keys.join(', ') : '(none)';
    }
    if (type === 'object') {
        if (details === '*' || details === true) return `${path} (all keys)`;
        if (Array.isArray(details)) return `${path} [${details.join(', ')}]`;
        return path;
    }
    if (type === 'array') {
        const trackBy = details?.trackBy || '?';
        const track = details?.track;
        const trackStr = track === '*' ? 'all fields' : Array.isArray(track) ? track.join(', ') : '?';
        return `${path} (by ${trackBy}) [${trackStr}]`;
    }
    return String(entry);
};

const CacheFieldsSection = ({ jobData, isAdmin, isArchived, trigger }) => {

    const { confirm, ConfirmModal } = useConfirmModal();

    const [fields, setFields] = useState([]);
    const [settingId, setSettingId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [savedFields, setSavedFields] = useState([]);

    const hasUnsavedChanges = JSON.stringify(fields) !== JSON.stringify(savedFields);

    // Add form
    const [addType, setAddType] = useState('scalar');
    const [addPath, setAddPath] = useState('');
    const [addTrackKeys, setAddTrackKeys] = useState([]);
    const [addTrackBy, setAddTrackBy] = useState('id');
    const [addTrackAll, setAddTrackAll] = useState(false);

    // Edit form
    const [editIndex, setEditIndex] = useState(null);
    const [editType, setEditType] = useState('scalar');
    const [editPath, setEditPath] = useState('');
    const [editTrackKeys, setEditTrackKeys] = useState([]);
    const [editTrackBy, setEditTrackBy] = useState('id');
    const [editTrackAll, setEditTrackAll] = useState(false);

    const jobId = jobData?.id || jobData?.job_id;

    const fetchCacheFields = () => {
        setLoading(true);
        ApiRequest('GET', '/dmt/jobs/{jobId}/globalSettings', {
            pathParams: { jobId },
            queryParams: { table: 'dmt_job_setting', conditions: '`key` = "cache_fields"' },
        }).then((res) => {
            if (res.success && Array.isArray(res.data) && res.data.length > 0) {
                const setting = res.data[0];
                setSettingId(setting.setting_id);
                let parsed = setting.value;
                if (typeof parsed === 'string') {
                    try { parsed = JSON.parse(parsed); } catch { parsed = []; }
                }
                const result = Array.isArray(parsed) ? parsed : [];
                setFields(result);
                setSavedFields(result);
            } else {
                setSettingId(null);
                setFields([]);
                setSavedFields([]);
            }
            setLoading(false);
        });
    };

    useEffect(() => {
        if (jobId) fetchCacheFields();
    }, [jobId]);

    const saveFields = (updatedFields) => {
        const valueToStore = JSON.stringify(updatedFields);

        if (settingId) {
            // Update existing
            ApiRequest('PATCH', '/dmt/jobs/{jobId}/globalSettings/{settingId}', {
                pathParams: { jobId, settingId },
                body: [{ op: 'replace', path: 'value', value: valueToStore }],
                queryParams: { table: 'dmt_job_setting' },
            }).then((res) => {
                if (res.success) {
                    ToastBuilder('success', 'Cache fields updated');
                    setFields(updatedFields);
                    setSavedFields(updatedFields);
                } else {
                    ToastBuilder('error', 'Failed to update cache fields');
                }
            });
        } else {
            // Create new
            ApiRequest('POST', '/dmt/jobs/{jobId}/globalSettings', {
                pathParams: { jobId },
                body: {
                    job_id: parseInt(jobId),
                    key: 'cache_fields',
                    value: valueToStore,
                    type: 'cache',
                    description: 'Fields tracked by the record state cache (Code 30)',
                },
                queryParams: { table: 'dmt_job_setting' },
            }).then((res) => {
                if (res.success) {
                    ToastBuilder('success', 'Cache fields created');
                    fetchCacheFields();
                } else {
                    ToastBuilder('error', 'Failed to create cache fields');
                }
            });
        }
    };

    const handleSave = () => saveFields(fields);
    const handleUndo = () => setFields(savedFields);

    const handleAdd = () => {
        const path = addPath.trim();
        if (!path && addType !== 'persistent') return ToastBuilder('error', 'Field path is required');

        // Check for duplicate
        if (addType !== 'persistent' && fields.some(f => getFieldPath(f) === path)) {
            return ToastBuilder('error', `Field "${path}" is already tracked`);
        }

        let newEntry;
        switch (addType) {
            case 'scalar':
                newEntry = path;
                break;
            case 'object':
                newEntry = { [path]: addTrackAll ? '*' : (addTrackKeys.length > 0 ? addTrackKeys : '*') };
                break;
            case 'array':
                newEntry = { [path]: { trackBy: addTrackBy || 'id', track: addTrackAll ? '*' : (addTrackKeys.length > 0 ? addTrackKeys : '*') } };
                break;
            case 'persistent':
                if (addTrackKeys.length === 0) return ToastBuilder('error', 'Add at least one persistent field key');
                // Check if _persistent_data_ already exists — merge keys
                const existingIdx = fields.findIndex(f => typeof f === 'object' && f !== null && '_persistent_data_' in f);
                if (existingIdx >= 0) {
                    const existing = fields[existingIdx]['_persistent_data_'] || [];
                    const merged = [...new Set([...existing, ...addTrackKeys])];
                    const updated = [...fields];
                    updated[existingIdx] = { '_persistent_data_': merged };
                    setFields(updated);
                    setAddTrackKeys([]);
                    return;
                }
                newEntry = { '_persistent_data_': addTrackKeys };
                break;
            default:
                newEntry = path;
        }

        setFields([...fields, newEntry]);
        setAddPath('');
        setAddTrackKeys([]);
        setAddTrackBy('id');
        setAddTrackAll(false);
    };

    const handleRemove = (index) => {
        setFields(fields.filter((_, i) => i !== index));
    };

    const handleMove = (index, direction) => {
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= fields.length) return;
        const updated = [...fields];
        [updated[index], updated[targetIndex]] = [updated[targetIndex], updated[index]];
        setFields(updated);
    };

    const handleEditStart = (index) => {
        const field = fields[index];
        const type = detectFieldType(field);
        const path = getFieldPath(field);
        const details = getFieldDetails(field);

        setEditIndex(index);
        setEditType(type);
        setEditPath(path);

        if (type === 'persistent') {
            setEditTrackAll(false);
            setEditTrackKeys(Array.isArray(details) ? details : []);
            setEditTrackBy('id');
        } else if (type === 'object') {
            if (details === '*' || details === true) {
                setEditTrackAll(true);
                setEditTrackKeys([]);
            } else {
                setEditTrackAll(false);
                setEditTrackKeys(Array.isArray(details) ? details : []);
            }
            setEditTrackBy('id');
        } else if (type === 'array') {
            setEditTrackBy(details?.trackBy || 'id');
            const track = details?.track;
            if (track === '*') {
                setEditTrackAll(true);
                setEditTrackKeys([]);
            } else {
                setEditTrackAll(false);
                setEditTrackKeys(Array.isArray(track) ? track : []);
            }
        } else {
            setEditTrackAll(false);
            setEditTrackKeys([]);
            setEditTrackBy('id');
        }
    };

    const handleEditApply = () => {
        const path = editPath.trim();
        if (!path && editType !== 'persistent') return ToastBuilder('error', 'Field path is required');

        // Check duplicate (excluding the field being edited)
        if (editType !== 'persistent' && fields.some((f, i) => i !== editIndex && getFieldPath(f) === path)) {
            return ToastBuilder('error', `Field "${path}" is already tracked`);
        }

        let newEntry;
        switch (editType) {
            case 'scalar':
                newEntry = path;
                break;
            case 'object':
                newEntry = { [path]: editTrackAll ? '*' : (editTrackKeys.length > 0 ? editTrackKeys : '*') };
                break;
            case 'array':
                newEntry = { [path]: { trackBy: editTrackBy || 'id', track: editTrackAll ? '*' : (editTrackKeys.length > 0 ? editTrackKeys : '*') } };
                break;
            case 'persistent':
                if (editTrackKeys.length === 0) return ToastBuilder('error', 'Add at least one persistent field key');
                newEntry = { '_persistent_data_': editTrackKeys };
                break;
            default:
                newEntry = path;
        }

        const updated = [...fields];
        updated[editIndex] = newEntry;
        setFields(updated);
        setEditIndex(null);
    };

    const handleEditCancel = () => {
        setEditIndex(null);
    };

    // Import JSON — bulk add/replace
    const [showImport, setShowImport] = useState(false);
    const [importText, setImportText] = useState('');

    const handleImport = async () => {
        const text = importText.trim();
        if (!text) return ToastBuilder('error', 'Paste a JSON array of field configs');
        try {
            const parsed = JSON.parse(text);
            if (!Array.isArray(parsed)) return ToastBuilder('error', 'Must be a JSON array');
            // Validate each entry
            for (const entry of parsed) {
                if (typeof entry !== 'string' && typeof entry !== 'object') {
                    return ToastBuilder('error', `Invalid entry: ${JSON.stringify(entry)}`);
                }
            }
            const mode = fields.length > 0 ? await confirm(
                `You have ${fields.length} existing field(s). Replace all, or merge (add new, keep existing)?`,
                { title: 'Import Mode', confirmLabel: 'Replace All', cancelLabel: 'Merge' }
            ) : true;

            let updated;
            if (mode) {
                // Replace
                updated = parsed;
            } else {
                // Merge — add entries whose paths don't already exist
                const existingPaths = new Set(fields.map(f => getFieldPath(f)));
                const newEntries = parsed.filter(e => !existingPaths.has(getFieldPath(e)));
                updated = [...fields, ...newEntries];
                if (newEntries.length === 0) return ToastBuilder('info', 'No new fields to add — all already exist');
            }
            setFields(updated);
            setImportText('');
            setShowImport(false);
            ToastBuilder('success', `Imported ${updated.length} field(s) — save to persist`);
        } catch (e) {
            ToastBuilder('error', `Invalid JSON: ${e.message}`);
        }
    };

    const [modal, setModal] = useState(false);

    const openModal = () => {
        fetchCacheFields();
        setModal(true);
    };

    const modalBody = (
        <div>
            <div className='d-flex justify-content-between align-items-center' style={{ marginBottom: 12 }}>
                <p style={{ fontSize: 12, color: '#666', margin: 0 }}>
                    Fields tracked by the record state cache. Used by Code 30 mapping for change detection.
                    Access via <code>args['record_state_cache']</code> during processing.
                </p>
                {!isArchived && (
                    <div className='d-flex gap-1'>
                        <Button size='sm' color={showImport ? 'secondary' : 'outline-secondary'} style={{ fontSize: 10 }}
                            onClick={() => { setShowImport(!showImport); if (!showImport) setImportText(JSON.stringify(fields, null, 2)); }}>
                            {showImport ? 'Cancel' : 'Import/Export JSON'}
                        </Button>
                    </div>
                )}
            </div>

            {/* Import/Export JSON */}
            {showImport && (
                <div style={{ marginBottom: 16, padding: 12, background: 'rgba(128,128,128,0.04)', borderRadius: 4, border: '1px solid rgba(128,128,128,0.15)' }}>
                    <label style={{ fontSize: 10, fontWeight: 600, marginBottom: 4, display: 'block' }}>JSON Array (paste to import, or copy to export)</label>
                    <Input type='textarea' value={importText} onChange={(e) => setImportText(e.target.value)}
                        style={{ fontFamily: 'monospace', fontSize: 11, minHeight: 120, marginBottom: 8 }}
                        placeholder='["status/name", {"requester_info": ["name", "email"]}, {"requested_items": {"trackBy": "id", "track": ["stage"]}}]' />
                    <div className='d-flex gap-2 justify-content-end'>
                        <Button size='sm' color='outline-info' onClick={() => { handleCopy(JSON.stringify(fields, null, 2)); ToastBuilder('success', 'Copied to clipboard'); }}>
                            Copy Current
                        </Button>
                        <Button size='sm' color='primary' onClick={handleImport}>Import</Button>
                    </div>
                </div>
            )}

            {/* Current fields */}
            {loading ? (
                <p style={{ textAlign: 'center', padding: 20, color: '#999' }}>Loading...</p>
            ) : fields.length === 0 ? (
                <p style={{ textAlign: 'center', padding: 20, color: '#999' }}>No cache fields configured. Add fields below to enable change tracking.</p>
            ) : (
                <div style={{ marginBottom: 16 }}>
                    {fields.map((field, index) => {
                        const type = detectFieldType(field);
                        const path = getFieldPath(field);

                        // Inline edit mode for this row
                        if (editIndex === index && !isArchived) {
                            return (
                                <div key={index} style={{ padding: '10px 12px', borderBottom: '1px solid rgba(128,128,128,0.1)', background: 'rgba(13,110,253,0.03)' }}>
                                    <div className='d-flex justify-content-between align-items-center' style={{ marginBottom: 8 }}>
                                        <span style={{ fontSize: 11, fontWeight: 600 }}>Editing: <code>{path}</code></span>
                                        <div className='d-flex gap-1'>
                                            {FIELD_TYPE_OPTIONS.map(t => (
                                                <Button key={t.value} size='sm' color={editType === t.value ? 'primary' : 'light'}
                                                    style={{ fontSize: 10, padding: '2px 8px' }}
                                                    onClick={() => setEditType(t.value)} title={t.description}>{t.label}</Button>
                                            ))}
                                        </div>
                                    </div>
                                    <Row className='align-items-end g-2 input-sm'>
                                        <Col md={editType === 'scalar' ? 8 : editType === 'array' ? 3 : 3}>
                                            <label style={{ fontSize: 10, fontWeight: 600 }}>Field Path</label>
                                            <Input bsSize='sm' value={editPath} onChange={(e) => setEditPath(e.target.value)}
                                                style={{ fontFamily: 'monospace' }} />
                                        </Col>
                                        {editType === 'array' && (
                                            <Col md={2}>
                                                <label style={{ fontSize: 10, fontWeight: 600 }}>Track By</label>
                                                <Input bsSize='sm' value={editTrackBy} onChange={(e) => setEditTrackBy(e.target.value)}
                                                    style={{ fontFamily: 'monospace' }} />
                                            </Col>
                                        )}
                                        {editType !== 'scalar' && (
                                            <Col md={editType === 'array' ? 4 : 6}>
                                                <div className='d-flex align-items-center justify-content-between'>
                                                    <label style={{ fontSize: 10, fontWeight: 600 }}>
                                                        {editType === 'array' ? 'Track Fields' : 'Track Keys'}
                                                    </label>
                                                    <label style={{ fontSize: 10, cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}>
                                                        <input type='checkbox' checked={editTrackAll} onChange={(e) => setEditTrackAll(e.target.checked)}
                                                            style={{ marginRight: 4 }} />
                                                        All
                                                    </label>
                                                </div>
                                                {!editTrackAll && (
                                                    <div className='input-sm'>
                                                        <CreatableMultiSelect value={editTrackKeys} onChange={setEditTrackKeys}
                                                            placeholder='Type key and press Enter...' size='sm' />
                                                    </div>
                                                )}
                                                {editTrackAll && (
                                                    <Input bsSize='sm' disabled value='* (all keys)' style={{ fontFamily: 'monospace', opacity: 0.5 }} />
                                                )}
                                            </Col>
                                        )}
                                        <Col md={2} className='d-flex align-items-end gap-1'>
                                            <Button size='sm' color='primary' style={{ fontSize: 10 }} onClick={handleEditApply}>Apply</Button>
                                            <Button size='sm' color='light' style={{ fontSize: 10 }} onClick={handleEditCancel}>Cancel</Button>
                                        </Col>
                                    </Row>
                                </div>
                            );
                        }

                        return (
                            <div key={index} style={{ padding: '6px 12px', borderBottom: '1px solid rgba(128,128,128,0.1)' }}
                                className='d-flex justify-content-between align-items-center'>
                                {!isArchived && (
                                    <div className='d-flex align-items-center' style={{ marginRight: 8, gap: 2 }}>
                                        <ArrowUp size={12} style={{ cursor: index === 0 ? 'default' : 'pointer', opacity: index === 0 ? 0.2 : 0.6 }}
                                            onClick={() => handleMove(index, -1)} title='Move up' />
                                        <ArrowDown size={12} style={{ cursor: index === fields.length - 1 ? 'default' : 'pointer', opacity: index === fields.length - 1 ? 0.2 : 0.6 }}
                                            onClick={() => handleMove(index, 1)} title='Move down' />
                                    </div>
                                )}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div className='d-flex align-items-center' style={{ gap: 6 }}>
                                        <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 500 }}>{formatFieldDisplay(field)}</span>
                                        <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: fieldTypeColor[type] }}>{type}</span>
                                        <Copy size={10} style={{ cursor: 'pointer', opacity: 0.3 }}
                                            onClick={() => handleCopy(path)} title={`Copy: ${path}`} />
                                    </div>
                                </div>
                                {!isArchived && (
                                    <div className='d-flex align-items-center gap-2'>
                                        <Button size='sm' color='link' className='p-0 txt-warning' style={{ fontSize: 11 }}
                                            onClick={() => handleEditStart(index)}>Edit</Button>
                                        <Button size='sm' color='link' className='p-0 text-danger' style={{ fontSize: 11 }}
                                            onClick={() => handleRemove(index)}>Del</Button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Save / Undo bar */}
            {hasUnsavedChanges && (
                <div className='d-flex align-items-center justify-content-between'
                    style={{ padding: '8px 12px', marginBottom: 12, background: 'rgba(13,110,253,0.06)', borderRadius: 4, border: '1px solid rgba(13,110,253,0.2)' }}>
                    <span style={{ fontSize: 11, color: '#0d6efd', fontWeight: 500 }}>You have unsaved changes</span>
                    <div className='d-flex gap-2'>
                        <Button size='sm' color='outline-secondary' style={{ fontSize: 11 }} onClick={handleUndo}>
                            <RotateCcw size={11} style={{ marginRight: 4 }} />Undo
                        </Button>
                        <Button size='sm' color='primary' style={{ fontSize: 11 }} onClick={handleSave}>
                            <Save size={11} style={{ marginRight: 4 }} />Save
                        </Button>
                    </div>
                </div>
            )}

            {/* Add field */}
            {!isArchived && (
                <div style={{ borderTop: '1px solid rgba(128,128,128,0.2)', paddingTop: 12 }}>
                    <div className='d-flex justify-content-between align-items-center' style={{ marginBottom: 8 }}>
                        <span style={{ fontSize: 12, fontWeight: 600 }}>Add Field</span>
                        <div className='d-flex gap-1'>
                            {FIELD_TYPE_OPTIONS.map(t => (
                                <Button key={t.value} size='sm' color={addType === t.value ? 'primary' : 'light'}
                                    style={{ fontSize: 10, padding: '2px 8px' }}
                                    onClick={() => setAddType(t.value)} title={t.description}>{t.label}</Button>
                            ))}
                        </div>
                    </div>

                    <Row className='align-items-end g-2 input-sm'>
                        <Col md={addType === 'scalar' ? 11 : 4}>
                            <label style={{ fontSize: 10, fontWeight: 600 }}>Field Path</label>
                            <Input bsSize='sm' placeholder='e.g., status/name or requester_info'
                                value={addPath} onChange={(e) => setAddPath(e.target.value)}
                                style={{ fontFamily: 'monospace' }} />
                        </Col>

                        {addType === 'array' && (
                            <Col md={2}>
                                <label style={{ fontSize: 10, fontWeight: 600 }}>Track By</label>
                                <Input bsSize='sm' placeholder='id' value={addTrackBy}
                                    onChange={(e) => setAddTrackBy(e.target.value)} style={{ fontFamily: 'monospace' }} />
                            </Col>
                        )}

                        {addType !== 'scalar' && (
                            <Col md={addType === 'array' ? 5 : 7}>
                                <div className='d-flex align-items-center justify-content-between'>
                                    <label style={{ fontSize: 10, fontWeight: 600 }}>
                                        {addType === 'array' ? 'Track Fields' : 'Track Keys'}
                                    </label>
                                    <label style={{ fontSize: 10, cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}>
                                        <input type='checkbox' checked={addTrackAll} onChange={(e) => setAddTrackAll(e.target.checked)}
                                            style={{ marginRight: 4 }} />
                                        All
                                    </label>
                                </div>
                                {!addTrackAll && (
                                    <div className='input-sm'>
                                        <CreatableMultiSelect value={addTrackKeys} onChange={setAddTrackKeys}
                                            placeholder='Type key and press Enter...' size='sm' />
                                    </div>
                                )}
                                {addTrackAll && (
                                    <Input bsSize='sm' disabled value='* (all keys)' style={{ fontFamily: 'monospace', opacity: 0.5 }} />
                                )}
                            </Col>
                        )}

                        <Col md={1} className='d-flex align-items-end'>
                            <ButtonBuilder form='' label='+' className='btn-primary btn-sm px-3 py-1' style={{ fontSize: 18 }}
                                onClick={handleAdd} useSpinner={false} />
                        </Col>
                    </Row>
                </div>
            )}
        </div>
    );

    if (trigger) {
        return (
            <Fragment>
                <span onClick={openModal} style={{ display: 'inline-flex' }}>{trigger}</span>
                <CommonModal isOpen={modal} title='Cache Fields (Change Tracking)' toggler={() => setModal(false)} size='lg' onSubmit={false} onCancelLabel='Close'>
                    {modalBody}
                </CommonModal>
                <ConfirmModal />
            </Fragment>
        );
    }

    return (
        <Fragment>
            <ModalButtonBuilder
                title='Cache Fields'
                className='btn-outline-info'
                onSubmit={false}
                onCancelLabel='Close'
                size='lg'
                onClick={fetchCacheFields}
                body={modalBody}
            />
            <ConfirmModal />
        </Fragment>
    );
};

export default CacheFieldsSection;
